import { Box, ModalBody, ModalFoot, ModalHead } from '@dixa/lib-shared-habla';
import { useCallback } from 'react';
import { connect } from 'react-redux';
import {
  FieldArray,
  Form,
  formValueSelector,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';

import type { RootReducerStateT } from 'app/reducers/rootReducer';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { getRequestID } from 'components/shared/util/request';
import type { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import type {
  FlowCallNodeLocations,
  MatchingPayload,
  Payload,
  RulePayload,
} from '../nodeTypes/locations';

import { Rules } from './locations/rules';
import { validate } from './locations/validation';
import styles from './locations.module.scss';

export const LOCATIONS_FIELD = 'rules';

type StateProps = ReturnType<typeof mapStateToProps>;

type OwnProps = FlowManagerEditModalComponentT<FlowCallNodeLocations>;

type InjectedProps = InjectedFormProps<LocationsModalFormValues, Props>;

type Props = OwnProps & StateProps;

export interface LocationsModalFormValues {
  rules: RulePayload[];
}

const FORM_NAME = 'callFlowLocationsForm';

function LocationsImpl({
  node,
  nodeLabel,
  rules,
  handleSubmit,
  replaceNode,
  triggerClose,
  dirty: isChanged,
}: Props & InjectedProps): JSX.Element {
  const handleSaveClick = useCallback(
    ({ rules }: LocationsModalFormValues) => {
      const payload: Payload = {
        non_match_node_id: node.payload?.non_match_node_id || getRequestID(),
        rules: rules.map(({ name, match_node_id, matchings }: RulePayload) => ({
          name,
          match_node_id: match_node_id || getRequestID(),
          matchings: matchings.map((matching: MatchingPayload) => ({
            ...matching,
            rule: 'prefix',
            type: 'full-number',
          })),
          rule_type: 'any',
        })),
      };

      const nodeOptions: FlowCallNodeLocations = {
        ...node,
        icon: 'i-th',
        isAction: false,
        isEditable: true,
        isRemovable: true,
        nodeType: FlowNodeTypes.LOCATIONS,
        overlay: [],
        payload,
        text: 'Locations',
      };

      replaceNode(nodeOptions);
      triggerClose();
    },
    [node, replaceNode, triggerClose],
  );

  return (
    <Box width="600px">
      <ModalHead
        className={styles.head}
        onClose={triggerClose}
        title={nodeLabel}
      >
        <div className={styles.description}>
          Sort incoming calls based on country code. This is useful for general
          and specific locations (e.g. +44 for UK or +49035 for Dresden in
          Germany).
        </div>
      </ModalHead>
      <Form onSubmit={handleSubmit(handleSaveClick)}>
        <ModalBody className={styles.body}>
          <FieldArray name={LOCATIONS_FIELD} component={Rules} rules={rules} />
        </ModalBody>
        <ModalFoot
          cancelOnClick={triggerClose}
          confirmName={getConfirmButtonVerb(node)}
          confirmProps={{ disabled: !isChanged }}
          confirmOnClick={handleSubmit(handleSaveClick)}
        />
      </Form>
    </Box>
  );
}

const mapStateToProps = (state: RootReducerStateT, ownProps: OwnProps) => {
  const DEFAULT_PAYLOAD: Payload = {
    rules: [
      {
        name: '',
        matchings: [
          {
            type: 'full-number',
            rule: 'prefix',
            match: '',
          },
        ],
        rule_type: 'any',
      },
    ],
  };

  const payload = ownProps.node.payload || DEFAULT_PAYLOAD;

  const valueSelect = formValueSelector(FORM_NAME);

  return {
    initialValues: ownProps.node.payload || DEFAULT_PAYLOAD,
    payload,
    rules: valueSelect(state, 'rules') || payload.rules,
  };
};

export const Locations = connect(mapStateToProps)(
  reduxForm<LocationsModalFormValues, Props>({
    form: FORM_NAME,
    enableReinitialize: true,
    touchOnBlur: false,
    validate,
  })(LocationsImpl),
);
